import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import axios from "axios";
import { getDealerToken, updateDealerInfo, clearDealerSession } from "@/lib/dealerAuth";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function DealerDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("bids");
  const [dealer, setDealer] = useState(null);
  const [bids, setBids] = useState([]);
  const [wonBids, setWonBids] = useState([]);
  const [wonAuctions, setWonAuctions] = useState([]);
  const [bankSettings, setBankSettings] = useState({});
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [profileForm, setProfileForm] = useState({});
  const [saving, setSaving] = useState(false);

  const token = getDealerToken();

  const fetchData = useCallback(async () => {
    if (!token) { navigate("/dealer-login"); return; }
    setLoading(true);
    try {
      const [meRes, bidsRes, wonRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/dealer/me?token=${token}`),
        axios.get(`${BACKEND_URL}/api/dealer/my-bids?token=${token}`),
        axios.get(`${BACKEND_URL}/api/dealer/won-bids?token=${token}`),
      ]);
      setDealer(meRes.data);
      // Refresh the persisted dealer profile so CarDetail/DealerBidPanel see
      // up-to-date KYC / verified_dealer_badge without a re-login.
      updateDealerInfo(meRes.data);
      setProfileForm({
        full_name: meRes.data.full_name || "",
        phone: meRes.data.phone || "",
        company_name: meRes.data.company_name || "",
        company_address: meRes.data.company_address || "",
        country: meRes.data.country || "",
      });
      setBids(bidsRes.data);
      setWonBids(wonRes.data);
      // Phase 3: won auctions (independent from won-bids old collection)
      try {
        const wonAuctionRes = await axios.get(`${BACKEND_URL}/api/dealer/won-auctions?token=${token}`);
        setWonAuctions(wonAuctionRes.data || []);
      } catch { setWonAuctions([]); }
      try {
        const bs = await axios.get(`${BACKEND_URL}/api/settings/bank`);
        setBankSettings(bs.data || {});
      } catch { setBankSettings({}); }
    } catch (err) {
      if (err.response?.status === 401) {
        clearDealerSession();
        navigate("/dealer-login");
      }
    } finally {
      setLoading(false);
    }
  }, [token, navigate]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleLogout = () => {
    clearDealerSession();
    navigate("/");
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${BACKEND_URL}/api/dealer/profile?token=${token}`, profileForm);
      if (res.data.dealer) {
        updateDealerInfo(res.data.dealer);
      }
      setDealer(prev => ({ ...prev, ...profileForm }));
      setEditMode(false);
      toast.success("Profil frissítve!");
    } catch {
      toast.error("Hiba történt a mentés során");
    } finally {
      setSaving(false);
    }
  };

  const getThumbUrl = (url) => {
    if (!url) return null;
    let u = url.replace("_full.webp", "_thumb.webp");
    if (u === url && (url.endsWith(".jpeg") || url.endsWith(".jpg"))) {
      u = url.replace(/\.(jpeg|jpg)$/, "_thumb.webp");
    }
    return u?.startsWith("/api") ? `${BACKEND_URL}${u}` : u;
  };

  const formatDate = (d) => new Date(d).toLocaleDateString("hu-HU", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });

  // Group bids by car
  const bidsByCar = bids.reduce((acc, bid) => {
    if (!acc[bid.car_id]) acc[bid.car_id] = { car_name: bid.car_name, car_image: bid.car_image, car_id: bid.car_id, dealer_highest_bid: bid.dealer_highest_bid, dealer_auction_end_time: bid.dealer_auction_end_time, bids: [] };
    acc[bid.car_id].bids.push(bid);
    return acc;
  }, {});

  if (loading) return (
    <div className="min-h-screen bg-[#030303] flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-[#D4AF37] border-t-transparent rounded-full animate-spin" />
    </div>
  );

  const tabs = [
    { id: "bids", label: "Licitjeim", count: Object.keys(bidsByCar).length },
    { id: "won", label: "Nyertes licitek", count: wonBids.length },
    { id: "won-auctions", label: "Megnyert aukciók", count: wonAuctions.length },
    { id: "profile", label: "Profil", count: null },
  ];

  const auctionStatusLabel = (order) => {
    if (!order) return { text: "Aukció megnyerve", color: "text-green-400" };
    const ps = order.payment_status;
    if (ps === "under_review") return { text: "FIZETÉS ELLENŐRZÉS ALATT", color: "text-blue-400" };
    if (ps === "confirmed") return { text: "FIZETVE", color: "text-[#D4AF37]" };
    if (ps === "overdue") return { text: "FIZETÉSI HATÁRIDŐ LEJÁRT", color: "text-red-400" };
    if (ps === "failed") return { text: "FIZETÉS SIKERTELEN", color: "text-red-400" };
    if (ps === "cancelled") return { text: "TÖRÖLVE", color: "text-red-400" };
    return { text: "FIZETÉSRE VÁR", color: "text-yellow-400" };
  };

  const fmtCountdown = (deadlineIso) => {
    if (!deadlineIso) return null;
    const diff = Math.floor((new Date(deadlineIso).getTime() - now) / 1000);
    if (diff <= 0) return { text: "LEJÁRT", expired: true };
    const h = Math.floor(diff / 3600);
    const m = Math.floor((diff % 3600) / 60);
    const s = diff % 60;
    return { text: `${String(h).padStart(2,"0")}:${String(m).padStart(2,"0")}:${String(s).padStart(2,"0")}`, expired: false };
  };

  const copyRef = (orderNumber) => {
    const ref = `AlpineCar ${orderNumber}`;
    try { navigator.clipboard.writeText(ref); toast.success("Közlemény vágólapra másolva"); } catch (e) { /* ignore */ }
  };

  const uploadProof = async (orderId, file) => {
    if (!file) return;
    const token = getDealerToken();
    const fd = new FormData();
    fd.append("file", file);
    try {
      await axios.post(`${BACKEND_URL}/api/dealer/orders/${orderId}/payment-proof?token=${token}`, fd);
      toast.success("Fizetési bizonylat feltöltve — ellenőrzés alatt");
      // Reload won-auctions data
      const r = await axios.get(`${BACKEND_URL}/api/dealer/won-auctions?token=${token}`);
      setWonAuctions(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Feltöltési hiba");
    }
  };

  return (
    <div className="min-h-screen bg-[#030303] text-white">
      {/* Header */}
      <div className="border-b border-white/5 px-4 py-4">
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <img src="/alpine-car-logo.png" alt="Alpine Car" className="w-10 h-10 rounded-lg object-cover" />
            <span className="font-bold text-lg">Alpine Car</span>
          </Link>
          <div className="flex items-center gap-4">
            <span className="text-sm text-white/50 hidden sm:block">{dealer?.full_name}</span>
            <button onClick={handleLogout} className="text-xs text-white/40 hover:text-red-400 transition-colors" data-testid="logout-btn">
              Kijelentkezés
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-6">
        {/* Welcome */}
        <div className="mb-6">
          <h1 className="text-xl sm:text-2xl font-bold">Kereskedői felület</h1>
          <p className="text-sm text-white/40 mt-1">{dealer?.company_name} - {dealer?.eu_vat_number}</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 border-b border-white/5 overflow-x-auto">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              className={`py-3 px-4 text-sm font-medium border-b-2 transition-colors whitespace-nowrap ${
                activeTab === t.id ? "border-[#D4AF37] text-white" : "border-transparent text-white/40 hover:text-white/60"
              }`}
              data-testid={`tab-${t.id}`}>
              {t.label} {t.count !== null && <span className="ml-1 text-xs text-white/30">({t.count})</span>}
            </button>
          ))}
        </div>

        {/* Bids Tab */}
        {activeTab === "bids" && (
          <div className="space-y-4" data-testid="bids-panel">
            {Object.keys(bidsByCar).length === 0 ? (
              <div className="text-center py-16 bg-[#0A0A0A] rounded-xl border border-white/5">
                <svg className="w-12 h-12 mx-auto text-white/10 mb-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M12 8v4l3 3m6-3a9 9 0 1 1-18 0 9 9 0 0 1 18 0z"/></svg>
                <p className="text-white/40">Még nem licitált semmire</p>
                <Link to="/" className="inline-block mt-3 text-sm text-[#D4AF37] hover:underline">Autók böngészése</Link>
              </div>
            ) : (
              Object.values(bidsByCar).map(group => {
                const isExpired = group.dealer_auction_end_time && new Date(group.dealer_auction_end_time) < new Date();
                const myHighest = Math.max(...group.bids.map(b => b.amount));
                const isWinning = myHighest >= group.dealer_highest_bid;
                return (
                  <div key={group.car_id} className="bg-[#0A0A0A] border border-white/5 rounded-xl overflow-hidden" data-testid={`bid-group-${group.car_id}`}>
                    <div className="flex items-center gap-4 p-4 border-b border-white/5">
                      {group.car_image && (
                        <Link to={`/car/${group.car_id}`}>
                          <img src={getThumbUrl(group.car_image)} alt="" className="w-16 h-12 rounded-lg object-cover" />
                        </Link>
                      )}
                      <div className="flex-1 min-w-0">
                        <Link to={`/car/${group.car_id}`} className="font-semibold text-white hover:text-[#D4AF37] transition-colors text-sm">
                          {group.car_name}
                        </Link>
                        <div className="flex items-center gap-3 mt-1">
                          {isExpired ? (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-white/5 text-white/40">Lezárult</span>
                          ) : (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-[#D4AF37]/10 text-[#D4AF37]">Aktív</span>
                          )}
                          {isWinning ? (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-green-500/10 text-green-400">Vezető licit</span>
                          ) : (
                            <span className="text-xs px-2 py-0.5 rounded-full bg-red-500/10 text-red-400">Túllicitálva</span>
                          )}
                        </div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs text-white/40">Legmagasabb</div>
                        <div className="font-bold text-[#D4AF37]">{group.dealer_highest_bid?.toLocaleString("hu-HU")} €</div>
                      </div>
                    </div>
                    {/* Bid history */}
                    <div className="divide-y divide-white/5">
                      {group.bids.map(bid => (
                        <div key={bid.id} className="flex items-center justify-between px-4 py-2.5 text-sm">
                          <span className="text-white/40 text-xs">{formatDate(bid.created_at)}</span>
                          <span className={`font-semibold ${bid.is_highest ? "text-green-400" : "text-white/60"}`}>
                            {bid.amount.toLocaleString("hu-HU")} €
                            {bid.is_highest && <span className="ml-1.5 text-[10px] text-green-400/60">legmagasabb</span>}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* Won Bids Tab */}
        {activeTab === "won" && (
          <div className="space-y-4" data-testid="won-panel">
            {wonBids.length === 0 ? (
              <div className="text-center py-16 bg-[#0A0A0A] rounded-xl border border-white/5">
                <svg className="w-12 h-12 mx-auto text-white/10 mb-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M9 12l2 2 4-4m6 2a9 9 0 1 1-18 0 9 9 0 0 1 18 0z"/></svg>
                <p className="text-white/40">Még nincs nyertes licit</p>
                <p className="text-xs text-white/20 mt-1">A lezárult aukciók nyertes licitjei itt jelennek meg</p>
              </div>
            ) : (
              wonBids.map(w => (
                <div key={w.car_id} className="bg-[#0A0A0A] border border-green-500/20 rounded-xl p-4 flex items-center gap-4" data-testid={`won-${w.car_id}`}>
                  {w.car_image && (
                    <img src={getThumbUrl(w.car_image)} alt="" className="w-20 h-14 rounded-lg object-cover" />
                  )}
                  <div className="flex-1">
                    <Link to={`/car/${w.car_id}`} className="font-semibold text-white hover:text-[#D4AF37]">{w.car_name}</Link>
                    <p className="text-xs text-white/40 mt-0.5">Aukció lezárva: {formatDate(w.auction_end)}</p>
                  </div>
                  <div className="text-right">
                    <span className="text-xs text-green-400 block">Nyertes licit</span>
                    <span className="font-bold text-green-400 text-lg">{w.winning_amount.toLocaleString("hu-HU")} €</span>
                  </div>
                </div>
              ))
            )}
          </div>
        )}

        {/* Won Auctions Tab (Phase 3) */}
        {activeTab === "won-auctions" && (
          <div className="space-y-4" data-testid="won-auctions-panel">
            {wonAuctions.length === 0 ? (
              <div className="text-center py-16 bg-[#0A0A0A] rounded-xl border border-white/5">
                <svg className="w-12 h-12 mx-auto text-white/10 mb-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"><path d="M9 12l2 2 4-4m6 2a9 9 0 1 1-18 0 9 9 0 0 1 18 0z"/></svg>
                <p className="text-white/40">Még nincs megnyert aukció</p>
                <p className="text-xs text-white/20 mt-1">A megnyert aukciók + rendelések itt jelennek meg</p>
              </div>
            ) : (
              wonAuctions.map(w => {
                const status = auctionStatusLabel(w.order);
                const snap = w.order?.pricing_snapshot || {};
                const countdown = fmtCountdown(w.order?.payment_deadline_at);
                const ps = w.order?.payment_status;
                const canUpload = w.order && !["confirmed", "cancelled"].includes(ps) && !countdown?.expired;
                return (
                  <div key={w.car.id} className="bg-[#0A0A0A] border border-[#D4AF37]/20 rounded-xl p-4" data-testid={`won-auction-${w.car.id}`}>
                    <div className="flex items-start gap-4">
                      {w.car.images?.[0] && (
                        <img src={w.car.images[0]} alt="" className="w-24 h-16 rounded-lg object-cover" />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <Link to={`/car/${w.car.id}`} className="font-semibold text-white hover:text-[#D4AF37] text-sm">
                            {w.car.brand} {w.car.model} ({w.car.year})
                          </Link>
                          <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border border-current/20 ${status.color}`} data-testid={`won-auction-status-${w.car.id}`}>{status.text}</span>
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-2 text-[11px] text-white/60">
                          <div>Nyertes licit: <span className="text-white">{(w.car.winner_bid_amount || 0).toLocaleString("hu-HU")} €</span></div>
                          <div className="col-span-2 sm:col-span-1">Fizetendő teljes összeg: <span className="text-[#D4AF37] font-bold">{(snap.total || w.car.winner_bid_amount || 0).toLocaleString("hu-HU")} €</span></div>
                          {w.order && <div>Rendelés: <span className="font-mono text-white">{w.order.order_number}</span></div>}
                          <div>Aukció ID: <span className="font-mono text-white/70">{w.car.id.slice(0,8)}</span></div>
                        </div>

                        {w.order && ps !== "confirmed" && countdown && (
                          <div className="mt-3 flex items-center gap-2 text-[11px]">
                            <span className="text-white/50">Fizetési határidő:</span>
                            <span className={`font-mono font-bold ${countdown.expired ? "text-red-400" : "text-white"}`} data-testid={`payment-countdown-${w.order.id}`}>{countdown.text}</span>
                          </div>
                        )}

                        {w.order && ps !== "confirmed" && (
                          <div className="mt-3 p-3 bg-[#0F0F0F] rounded-lg border border-white/5 space-y-2">
                            <div className="text-[11px] font-semibold text-white/80">Fizetési információk (banki átutalás)</div>
                            {bankSettings.eur_iban && (
                              <div className="text-[10px] text-white/60"><b>EUR utalás (Osztrák számla):</b> <span className="font-mono text-white">{bankSettings.eur_iban}</span></div>
                            )}
                            {bankSettings.huf_iban && (
                              <div className="text-[10px] text-white/60"><b>HUF utalás (Magyar számla):</b> <span className="font-mono text-white">{bankSettings.huf_iban}</span></div>
                            )}
                            <div className="text-[10px] text-white/60 flex items-center gap-2">
                              <b>Közlemény:</b> <span className="font-mono text-[#D4AF37]">AlpineCar {w.order.order_number}</span>
                              <button
                                type="button"
                                onClick={() => copyRef(w.order.order_number)}
                                className="px-2 py-0.5 rounded bg-[#D4AF37]/10 text-[#D4AF37] hover:bg-[#D4AF37]/20 text-[10px]"
                                data-testid={`copy-ref-${w.order.id}`}
                              >Másolás</button>
                            </div>

                            <div className="flex items-center gap-2 pt-2 border-t border-white/5">
                              {w.order.payment_proof_url ? (
                                <>
                                  <span className="text-[10px] text-green-400">Fizetési bizonylat feltöltve</span>
                                  <a href={w.order.payment_proof_url} target="_blank" rel="noopener noreferrer"
                                     className="text-[10px] text-[#D4AF37] hover:underline"
                                     data-testid={`view-proof-${w.order.id}`}>Megtekintés</a>
                                </>
                              ) : (
                                <label className={`px-3 py-1.5 rounded text-[10px] font-medium cursor-pointer ${canUpload ? "bg-[#D4AF37] text-black hover:opacity-90" : "bg-white/10 text-white/40 cursor-not-allowed"}`} data-testid={`upload-proof-label-${w.order.id}`}>
                                  <input type="file" accept="image/*,.pdf" className="hidden" disabled={!canUpload}
                                         onChange={(e) => uploadProof(w.order.id, e.target.files[0])}
                                         data-testid={`upload-proof-${w.order.id}`} />
                                  FIZETÉSI BIZONYLAT FELTÖLTÉSE
                                </label>
                              )}
                            </div>
                          </div>
                        )}

                        {w.order && (
                          <div className="mt-3">
                            <Link
                              to="/dealer/dashboard"
                              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg bg-[#D4AF37]/10 text-[#D4AF37] hover:bg-[#D4AF37]/20 text-xs font-medium"
                              data-testid={`view-order-${w.car.id}`}
                            >MEGTEKINTÉS &rarr;</Link>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* Profile Tab */}
        {activeTab === "profile" && dealer && (
          <div className="max-w-lg" data-testid="profile-panel">
            <div className="bg-[#0A0A0A] border border-white/5 rounded-xl p-5 space-y-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="font-semibold">Profil adatok</h3>
                {!editMode && (
                  <button onClick={() => setEditMode(true)} className="text-xs text-[#D4AF37] hover:underline" data-testid="edit-profile-btn">
                    Szerkesztés
                  </button>
                )}
              </div>

              {editMode ? (
                <div className="space-y-3">
                  {[
                    { key: "full_name", label: "Teljes név" },
                    { key: "phone", label: "Telefonszám" },
                    { key: "company_name", label: "Cégnév" },
                    { key: "company_address", label: "Cím" },
                    { key: "country", label: "Ország" },
                  ].map(f => (
                    <div key={f.key}>
                      <label className="block text-xs text-white/50 mb-1">{f.label}</label>
                      <input value={profileForm[f.key] || ""} onChange={(e) => setProfileForm(prev => ({ ...prev, [f.key]: e.target.value }))}
                        className="w-full bg-[#111] border border-white/10 rounded-lg px-3 py-2.5 text-sm focus:border-[#D4AF37] focus:outline-none"
                        data-testid={`profile-input-${f.key}`}
                      />
                    </div>
                  ))}
                  <div className="flex gap-2 pt-2">
                    <button onClick={handleSaveProfile} disabled={saving}
                      className="px-4 py-2 bg-[#D4AF37] text-black text-sm font-semibold rounded-lg hover:bg-[#c4a030] disabled:opacity-50"
                      data-testid="save-profile-btn">
                      {saving ? "Mentés..." : "Mentés"}
                    </button>
                    <button onClick={() => setEditMode(false)} className="px-4 py-2 bg-white/5 text-white/60 text-sm rounded-lg hover:bg-white/10">
                      Mégse
                    </button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3">
                  {[
                    { label: "Név", value: dealer.full_name },
                    { label: "Email", value: dealer.email },
                    { label: "Telefon", value: dealer.phone },
                    { label: "Cégnév", value: dealer.company_name },
                    { label: "Cím", value: dealer.company_address },
                    { label: "Ország", value: dealer.country },
                    { label: "ÁFA szám", value: dealer.eu_vat_number },
                  ].map(f => (
                    <div key={f.label} className="flex justify-between items-center py-1.5 border-b border-white/5 last:border-0">
                      <span className="text-xs text-white/40">{f.label}</span>
                      <span className="text-sm text-white">{f.value || "-"}</span>
                    </div>
                  ))}
                  <div className="pt-2">
                    {dealer.verified_dealer_badge ? (
                      <span
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-green-500/10 text-green-400 border border-green-500/20"
                        data-testid="dealer-verified-badge"
                        title={`Verified Dealer • KYC: ${dealer.kyc_status || "verified"}`}
                      >
                        <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 12l2 2 4-4m6 2a9 9 0 1 1-18 0 9 9 0 0 1 18 0z"/></svg>
                        Verified Dealer
                      </span>
                    ) : (
                      <span
                        className="inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full bg-white/5 text-white/50 border border-white/10"
                        data-testid="dealer-kyc-status"
                      >
                        KYC: {dealer.kyc_status || "pending"}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
